import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { systemSecurityApi } from '../../lib/api'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { tx } from '../../lib/i18n'

/** super_admin: force every admin to sign in with Google Authenticator (TOTP). */
const SecuritySettingsCard: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()
  const { data } = useQuery({
    queryKey: ['system-security'],
    queryFn: () => systemSecurityApi.get().then((r) => r.data.data!),
  })
  const save = useMutation({
    mutationFn: (require2fa: boolean) => systemSecurityApi.update(require2fa),
    onSuccess: (_r, require2fa) => {
      queryClient.invalidateQueries({ queryKey: ['system-security'] })
      showToast({
        type: 'success',
        message: require2fa ? 'บังคับ 2FA แล้ว — คนที่ยังไม่ได้ตั้งจะต้องสแกน QR ตอนเข้าสู่ระบบครั้งถัดไป' : 'ยกเลิกการบังคับ 2FA แล้ว',
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => showToast({ type: 'error', message: e?.response?.data?.error ?? 'Failed to save' }),
  })
  const on = data?.require2fa ?? false

  return (
    <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <ShieldCheck className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">{tx('Sign-in security (2FA)')}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              บังคับให้ผู้ดูแลทุกคนเข้าสู่ระบบด้วยรหัสจาก Google Authenticator
              {data && typeof data.adminsTotal === 'number' ? ` · ตั้งแล้ว ${data.adminsWith2fa}/${data.adminsTotal} คน` : ''}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              ตั้งค่าของตัวเองได้ที่{' '}
              <Link to="/account" className="text-indigo-600 hover:underline">
                {tx('My Account')}
              </Link>
            </p>
          </div>
        </div>
        <button
          onClick={() => save.mutate(!on)}
          disabled={save.isPending || !data || !can('system-settings.update')}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
            on ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
          title={on ? 'Required' : 'Optional'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
    </div>
  )
}

export default SecuritySettingsCard
